import { createContext, useContext, type ReactNode } from 'react';
import type { PageContext } from 'vike/types';

/**
 * Makes Vike's `pageContext` available to components.
 *
 * Vike core does not provide a React hook — that comes from `vike-react`, which
 * this project does not use because the SSR tier needs direct control over the
 * document shell (`lang`, `dir`, the pre-paint theme script and its CSP nonce).
 * This is the small piece of that convenience worth keeping.
 */
const Context = createContext<PageContext | null>(null);

export function PageContextProvider({
  value,
  children,
}: {
  value: PageContext;
  children: ReactNode;
}) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function usePageContext(): PageContext {
  const value = useContext(Context);
  if (!value) {
    throw new Error('usePageContext was called outside PageContextProvider');
  }
  return value;
}

/**
 * What this route's `+data.ts` returned.
 *
 * Vike core runs `data()` on both the server and, for a client-side
 * navigation, a background request to it — the type is not narrowed to the
 * calling page, so the cast is on the caller. A page that has no `+data.ts`
 * must not call this.
 */
export function useData<T>(): T {
  return usePageContext().data as T;
}

/**
 * The URL being rendered.
 *
 * `window.location` does not exist during server rendering, and the navigation
 * and language switcher both need to know where they are in order to emit the
 * right links in the first HTML response.
 */
export function useCurrentUrl(): string {
  return usePageContext().urlOriginal;
}
